import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client/react';
import { ReactNode, useMemo } from 'react';
import { Observable } from 'rxjs';

import { useAppConfig } from './app-config-provider';

import { REFRESH_ACCESS_TOKEN } from '#src/graphql/mutations/login';

function is401(error: import('@apollo/client').ErrorLike): boolean {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const statusCode = (error as { statusCode?: number }).statusCode;
    if (statusCode === 401) return true;
  }
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some(
      (e) =>
        (e.extensions?.code as string) === 'UNAUTHENTICATED' ||
        (e.message?.toLowerCase().includes('unauthorized') ?? false),
    );
  }
  return error?.message?.toLowerCase().includes('unauthorized') ?? false;
}

function createApolloClient(graphqlUrl: string) {
  const clientRef: { current: ApolloClient | null } = { current: null };

  let refreshPromise: Promise<unknown> | null = null;

  const errorLink = onError(({ error, operation, forward }) => {
    if (!is401(error)) {
      return forward(operation);
    }
    const context = operation.getContext?.() ?? {};
    // Allow callers to explicitly opt out of auth-refresh behavior for a given
    // operation (e.g. initial Me check, refresh mutation itself, or public
    // queries where 401s are expected and should not trigger refresh).
    if (context.skipAuthRefresh) {
      return forward(operation);
    }

    const client = clientRef.current;
    if (!client) {
      return forward(operation);
    }
    if (!refreshPromise) {
      refreshPromise = client
        .mutate({
          mutation: REFRESH_ACCESS_TOKEN,
          context: { skipAuthRefresh: true },
        })
        .then(() => {
          refreshPromise = null;
        })
        .catch((err: unknown) => {
          refreshPromise = null;
          throw err;
        });
    }
    const currentRefresh = refreshPromise;
    return new Observable((subscriber) => {
      currentRefresh
        .then(() => {
          const obs = forward(operation);
          obs.subscribe(subscriber);
        })
        .catch((err: unknown) => {
          client.resetStore();
          subscriber.error(err);
        });
    });
  });

  const httpLink = createHttpLink({
    uri: graphqlUrl,
    credentials: 'include',
  });

  const client = new ApolloClient({
    link: ApolloLink.from([errorLink, httpLink]),
    cache: new InMemoryCache(),
  });

  clientRef.current = client;
  return client;
}

interface ApolloProviderProps {
  children: ReactNode;
}

export function ApolloProvider({ children }: ApolloProviderProps) {
  const { config } = useAppConfig();
  const client = useMemo(
    () => createApolloClient(config.graphqlUrl),
    [config.graphqlUrl],
  );

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
}
